// ── Goal Colouring (shared by every reporting page) ──────────────────
//
// One rule everywhere, so a colour means the same thing on every board:
//   green  — at or past the goal
//   amber  — within 20% of the goal (80–99% of a "higher is better" goal,
//            or up to 20% over a "lower is better" one like error rate)
//   red    — further away than that
// Mirrors the marketing suite's benchmark grading (`lib/marketing/grading.ts`).

export type GoalStatus = "met" | "close" | "behind" | "none";

/** A "higher is better" goal counts as close from 80% of target. */
const CLOSE_RATIO_HIGHER = 0.8;
/** A "lower is better" goal (error rate, cost, days) tolerates 20% over. */
const CLOSE_RATIO_LOWER = 1.2;

/**
 * Where `actual` sits against `goal`. `comparison` is "gte" when more is
 * better (the default) and "lte" when less is better.
 */
export function goalStatus(
  actual: number | null | undefined,
  goal: number | null | undefined,
  comparison: "gte" | "lte" = "gte",
): GoalStatus {
  if (actual === null || actual === undefined || !Number.isFinite(actual)) return "none";
  if (goal === null || goal === undefined || !Number.isFinite(goal)) return "none";

  if (comparison === "gte") {
    if (actual >= goal) return "met";
    if (goal > 0 && actual >= goal * CLOSE_RATIO_HIGHER) return "close";
    return "behind";
  }
  if (actual <= goal) return "met";
  if (goal > 0 && actual <= goal * CLOSE_RATIO_LOWER) return "close";
  return "behind";
}

/** Text colour for a number being compared to the goal it should have hit by now. */
export const GOAL_TEXT: Record<GoalStatus, string> = {
  met: "text-green-600",
  close: "text-amber-600",
  behind: "text-red-600",
  none: "",
};

/**
 * Text colour for year-to-date progress against an ANNUAL goal. Being under
 * the annual number partway through the year is normal, so "behind" stays
 * neutral rather than red — only met and close get a colour.
 */
export const GOAL_TEXT_YTD: Record<GoalStatus, string> = {
  met: "text-emerald-600",
  close: "text-amber-600",
  behind: "text-muted-foreground",
  none: "text-muted-foreground",
};

/** Progress-bar fill matching GOAL_TEXT_YTD. */
export const GOAL_BAR: Record<GoalStatus, string> = {
  met: "bg-emerald-500",
  close: "bg-amber-500",
  behind: "bg-accent",
  none: "bg-accent",
};
